import type { WebinarRoomPlaybackDto } from '../types'

/** The holding slate's video (ORB-1118): what plays it, and whether each viewer sees it once or on a loop. */
export interface WaitingVideo {
    playbackId: string
    token: string
    playsOnce: boolean
}

/** What the holding slate shows behind its countdown: the web's waiting video, or the image set instead of one. */
export type Waiting = ({ kind: 'video' } & WaitingVideo) | { kind: 'image'; url: string }

/**
 * The join's waiting video or image, or null when it brought neither: the video is not ready, or the web has none. The
 * server never sets both; were it to, the video would show, as the one an editor has to remove to add the image.
 */
export function waitingOf(playback: Pick<WebinarRoomPlaybackDto, 'Waiting' | 'WaitingImageUrl'>): Waiting | null {
    const video = playback.Waiting
    if (video) return { kind: 'video', playbackId: video.PlaybackId, token: video.Token, playsOnce: video.PlaysOnce }
    return playback.WaitingImageUrl ? { kind: 'image', url: playback.WaitingImageUrl } : null
}

/**
 * Whether the video starts again as it ends: never when it plays once, and on a loop only while a whole play still
 * finishes before the start, so the last stretch shows the countdown alone rather than a play cut off part way.
 */
export function playsAgain(playsOnce: boolean, durationSeconds: number | null, startMs: number | null, nowMs: number): boolean {
    if (playsOnce || durationSeconds === null || startMs === null) return false
    return startMs - nowMs >= durationSeconds * 1000
}

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

/**
 * Whether the video waits for Play: the viewer's system asks for less motion, and nothing should move for longer than
 * five seconds unasked (WCAG 2.2.2). Called on the view, since an unbound `matchMedia` throws.
 */
export function startsPaused(view: Pick<Window, 'matchMedia'>): boolean {
    return typeof view.matchMedia === 'function' && view.matchMedia(REDUCED_MOTION_QUERY).matches
}
